import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Building2,
  CheckCircle2,
  Clock,
  Search,
  AlertCircle,
  FileCheck2,
  FileLock2,
  Loader2,
  Plus,
  Settings2,
  Upload,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from 'lib/utils';
import { useCompany } from 'contexts/CompanyContext';
import { buildCompanyScopedRoute } from 'utils/companyRoute';
import { resolveCompanyLifecycle } from 'utils/companyLifecycle';
import { ROUTES } from 'constants/routes';

const STATUS_CONFIG = {
  delivered: {
    icon: CheckCircle2,
    iconName: 'check-circle-2',
    label: 'empresas.stages.incorporated',
    status: 'incorporated',
    tone: 'success',
  },
  completed: {
    icon: CheckCircle2,
    iconName: 'check-circle-2',
    label: 'empresas.stages.incorporated',
    status: 'incorporated',
    tone: 'success',
  },
  dissolved: {
    icon: FileLock2,
    iconName: 'file-lock-2',
    color: 'text-[var(--rely-text-muted)]',
    bg: 'bg-[var(--rely-surface-elevated)]',
    label: 'empresas.stages.dissolved',
    status: 'dissolved',
    tone: 'neutral',
  },
  filed: {
    icon: FileCheck2,
    iconName: 'file-check-2',
    label: 'empresas.stages.filed',
    status: 'filed',
    tone: 'info',
  },
  filing: {
    icon: Clock,
    iconName: 'clock',
    label: 'empresas.stages.inFormation',
    status: 'filing',
    tone: 'attention',
  },
  details_pending: {
    icon: Clock,
    iconName: 'clock',
    label: 'empresas.stages.detailsPending',
    status: 'details_pending',
    tone: 'info',
  },
  under_review: {
    icon: Clock,
    iconName: 'clock',
    label: 'empresas.stages.underReview',
    status: 'under_review',
    tone: 'info',
  },
  approved: {
    icon: CheckCircle2,
    iconName: 'check-circle-2',
    label: 'import.statusSection.status.approved',
    status: 'approved',
    tone: 'success',
  },
  payment_completed: {
    icon: Clock,
    iconName: 'clock',
    label: 'empresas.stages.paymentCompleted',
    status: 'payment_completed',
    tone: 'info',
  },
  payment_pending: {
    icon: AlertCircle,
    iconName: 'alert-circle',
    label: 'empresas.stages.paymentPending',
    status: 'payment_pending',
    tone: 'attention',
  },
  default: {
    icon: Clock,
    iconName: 'clock',
    label: 'empresas.stages.inProcess',
    status: 'in_process',
    tone: 'neutral',
  },
};

const selectorStatusToneClasses = {
  success:
    'border-[var(--success)]/35 bg-[var(--success)]/10 text-[var(--success)]',
  attention:
    'border-[var(--warning)]/35 bg-[var(--warning)]/10 text-[var(--warning)]',
  info: 'border-[var(--info)]/35 bg-[var(--info)]/10 text-[var(--info)]',
  neutral:
    'border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] text-[var(--rely-text-muted)]',
};

function getStatusConfig(company) {
  const lifecycle = resolveCompanyLifecycle(company);
  const configKey = {
    active: 'delivered',
    dissolved: 'dissolved',
    filed: 'filed',
    filing: 'filing',
    details_pending: 'details_pending',
    under_review: 'under_review',
    approved: 'approved',
    payment_completed: 'payment_completed',
    payment_processing: 'payment_completed',
    payment_pending: 'payment_pending',
  }[lifecycle.id];

  return STATUS_CONFIG[configKey] || STATUS_CONFIG.default;
}

// Company initial for avatar
function getCompanyInitial(name) {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
}

export default function CompanySelector({ collapsed = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    companies,
    selectedCompany,
    setSelectedCompany,
    isLoading,
    hasCompanies,
  } = useCompany();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter companies by search
  const filteredCompanies = companies.filter((company) =>
    company.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current && companies.length > 3) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen, companies.length]);

  // Handle company selection - stay on current page, just switch company
  const handleSelectCompany = (company) => {
    setSelectedCompany(company.id);
    setIsOpen(false);
    setSearchQuery('');

    navigate(buildCompanyScopedRoute(location.pathname, company.id));
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  // Get dropdown position
  const getDropdownPosition = () => {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Calculate width based on sidebar state and viewport
    let width = collapsed ? Math.min(320, viewportWidth - 32) : rect.width;

    // Calculate left position
    let left = collapsed ? rect.right + 8 : rect.left;

    // Ensure dropdown doesn't go off-screen
    if (left + width > viewportWidth - 16) {
      left = viewportWidth - width - 16;
    }

    return {
      top: rect.top,
      left,
      width,
      maxHeight: `calc(100vh - ${rect.top + 16}px)`,
    };
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        data-testid="company-selector-loading"
        data-density="compact"
        data-surface="company-selector"
        className={cn(
          'flex items-center justify-center rounded-[var(--rely-radius-surface)]',
          'border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)]',
          collapsed ? 'mx-auto h-11 w-11' : 'mx-auto h-12 w-[calc(100%-1.5rem)]'
        )}
      >
        <Loader2
          className="h-4 w-4 animate-spin text-[var(--rely-text-muted)]"
          aria-hidden="true"
        />
      </div>
    );
  }

  // No companies state
  if (!hasCompanies) {
    return (
      <div
        data-testid="company-selector-empty"
        data-density="compact"
        data-surface="company-selector"
        className={cn(
          'flex items-center rounded-[var(--rely-radius-surface)]',
          'border border-dashed border-primary-300/80 bg-[var(--rely-surface-selected)]',
          'text-[var(--rely-text-muted)]',
          collapsed
            ? 'mx-auto h-11 w-11 justify-center'
            : 'mx-auto w-[calc(100%-1.5rem)] gap-2.5 px-3 py-2.5'
        )}
      >
        <Building2
          className={cn('flex-shrink-0', collapsed ? 'h-4 w-4' : 'h-5 w-5')}
        />
        {!collapsed && (
          <span className="text-[13px] font-medium">
            {t('sidebar.noCompanies')}
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        data-testid="company-selector-trigger"
        data-density="compact"
        data-surface="company-selector"
        data-state={isOpen ? 'open' : 'closed'}
        className={cn(
          'group relative flex items-center',
          'rounded-[var(--rely-radius-surface)] transition-[background-color,border-color,box-shadow,transform] duration-200',
          'border text-[var(--rely-text-strong)]',
          isOpen
            ? 'border-[var(--rely-color-brand-green)] bg-[var(--rely-surface-selected)] shadow-[var(--rely-shadow-selected)]'
            : 'border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] hover:border-primary-300 hover:bg-[var(--rely-surface-soft)] dark:hover:border-primary-800',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--rely-surface-panel)]',
          collapsed
            ? 'mx-auto h-11 w-11 justify-center'
            : 'mx-auto w-[calc(100%-1.5rem)] gap-2.5 px-3 py-2.5'
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        title={collapsed ? selectedCompany?.name : undefined}
      >
        {/* Company Avatar */}
        <div
          data-testid="company-selector-avatar"
          data-avatar="company-initial"
          className={cn(
            'flex flex-shrink-0 items-center justify-center rounded-md font-semibold',
            'bg-[var(--rely-color-brand-green)]',
            'text-[var(--rely-color-brand-black)] shadow-sm',
            collapsed ? 'h-7 w-7 text-xs' : 'h-8 w-8 text-sm'
          )}
        >
          {getCompanyInitial(selectedCompany?.name)}
        </div>

        {/* Company Info */}
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-[13px] font-semibold text-[var(--rely-text-strong)]">
                {selectedCompany?.name}
              </p>
            </div>

            {/* Chevron */}
            <ChevronDown
              className={cn(
                'h-4 w-4 flex-shrink-0 text-[var(--rely-text-muted)] transition-transform duration-200',
                'group-hover:text-[var(--rely-text-strong)]',
                isOpen && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </>
        )}
      </button>

      {/* Dropdown Portal */}
      {isOpen &&
        createPortal(
          <AnimatePresence>
            <motion.div
              ref={dropdownRef}
              data-testid="company-selector-dropdown"
              data-density="compact"
              data-surface="company-selector-menu"
              initial={{
                opacity: 0,
                x: collapsed ? -8 : 0,
                y: collapsed ? 0 : -8,
                scale: 0.96,
              }}
              animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
              exit={{
                opacity: 0,
                x: collapsed ? -8 : 0,
                y: collapsed ? 0 : -8,
                scale: 0.96,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              style={getDropdownPosition()}
              className={cn(
                'fixed z-[var(--rely-z-overlay)] flex flex-col overflow-hidden rounded-[var(--rely-radius-panel)]',
                'border border-[var(--rely-border-subtle)]',
                'bg-[var(--rely-surface-panel)] text-[var(--rely-text-strong)] shadow-[var(--rely-shadow-panel)]'
              )}
              onKeyDown={handleKeyDown}
            >
              {/* Header */}
              <div className="border-b border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] px-4 py-3">
                <h3 className="text-[11px] font-semibold leading-none text-[var(--rely-text-muted)]">
                  {t('sidebar.selectCompany')}
                </h3>
              </div>

              {/* Search - Only show if more than 3 companies */}
              {companies.length > 3 && (
                <div className="border-b border-[var(--rely-border-subtle)] p-2">
                  <div className="relative">
                    <Search
                      className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--rely-text-muted)]"
                      aria-hidden="true"
                    />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('sidebar.searchCompanies')}
                      className={cn(
                        'w-full rounded-lg py-2.5 pl-10 pr-3 text-sm',
                        'bg-[var(--rely-surface-elevated)]',
                        'text-[var(--rely-text-strong)]',
                        'placeholder:text-[var(--rely-text-muted)]',
                        'border border-[var(--rely-border-subtle)]',
                        'focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/20'
                      )}
                    />
                  </div>
                </div>
              )}

              {/* Companies List */}
              <div
                className="custom-scrollbar flex-1 overflow-y-auto p-2"
                style={{ maxHeight: '320px' }}
                role="listbox"
                aria-label={t('sidebar.selectCompany')}
              >
                {filteredCompanies.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-[var(--rely-text-muted)]">
                    <Search className="mb-2 h-8 w-8" aria-hidden="true" />
                    <span className="text-sm">{t('noResults')}</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredCompanies.map((company) => {
                      const isSelected = company.id === selectedCompany?.id;
                      const companyStatus = getStatusConfig(company);
                      const CompanyStatusIcon = companyStatus.icon;

                      return (
                        <button
                          key={company.id}
                          data-testid={`company-selector-option-${company.id}`}
                          data-density="compact"
                          data-state={isSelected ? 'selected' : 'available'}
                          onClick={() => handleSelectCompany(company)}
                          className={cn(
                            'flex min-h-[3.25rem] w-full items-center gap-3 rounded-[var(--rely-radius-surface)] border px-3 py-2 text-left',
                            'transition-[background-color,border-color,box-shadow] duration-150',
                            isSelected
                              ? 'border-[var(--rely-color-brand-green)] bg-[var(--rely-surface-selected)] shadow-[var(--rely-shadow-selected)]'
                              : 'border-transparent hover:border-[var(--rely-border-subtle)] hover:bg-[var(--rely-surface-elevated)]'
                          )}
                          role="option"
                          aria-selected={isSelected}
                        >
                          {/* Company Avatar */}
                          <div
                            className={cn(
                              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold',
                              isSelected
                                ? 'bg-[var(--rely-color-brand-green)] text-[var(--rely-color-brand-black)] shadow-sm'
                                : 'bg-[var(--rely-surface-soft)] text-[var(--rely-text-muted)]'
                            )}
                          >
                            {getCompanyInitial(company.name)}
                          </div>

                          {/* Company Info */}
                          <div className="min-w-0 flex-1 text-left">
                            <p
                              className={cn(
                                'truncate text-sm font-semibold',
                                isSelected
                                  ? 'text-[var(--rely-text-strong)]'
                                  : 'text-[var(--rely-text-strong)]'
                              )}
                            >
                              {company.name}
                            </p>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              <span
                                data-icon-name={companyStatus.iconName}
                                data-status={companyStatus.status}
                                data-tone={companyStatus.tone}
                                aria-label={t(companyStatus.label)}
                                className={cn(
                                  'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                                  selectorStatusToneClasses[
                                    companyStatus.tone
                                  ] || selectorStatusToneClasses.neutral
                                )}
                              >
                                <CompanyStatusIcon
                                  className="h-3 w-3"
                                  aria-hidden="true"
                                />
                              </span>
                            </div>
                          </div>

                          {/* Selected Indicator */}
                          {isSelected && (
                            <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 shadow-sm">
                              <CheckCircle2
                                className="h-4 w-4 text-white"
                                aria-hidden="true"
                              />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="border-t border-[var(--rely-border-subtle)] p-2">
                <div className="grid gap-1">
                  <Link
                    to={ROUTES.NUEVA_EMPRESA}
                    onClick={() => setIsOpen(false)}
                    className="flex min-h-11 items-center gap-3 rounded-[var(--rely-radius-surface)] px-3 text-sm font-medium text-[var(--rely-text-strong)] transition-colors hover:bg-[var(--rely-surface-selected)]"
                  >
                    <Plus className="h-4 w-4 text-[var(--rely-text-muted)]" aria-hidden="true" />
                    {t('navigation.createCompany')}
                  </Link>
                  <Link
                    to={ROUTES.IMPORT_EMPRESA}
                    onClick={() => setIsOpen(false)}
                    className="flex min-h-11 items-center gap-3 rounded-[var(--rely-radius-surface)] px-3 text-sm font-medium text-[var(--rely-text-strong)] transition-colors hover:bg-[var(--rely-surface-selected)]"
                  >
                    <Upload className="h-4 w-4 text-[var(--rely-text-muted)]" aria-hidden="true" />
                    {t('navigation.importCompany')}
                  </Link>
                  <Link
                    to={ROUTES.EMPRESAS}
                    onClick={() => setIsOpen(false)}
                    className="flex min-h-11 items-center gap-3 rounded-[var(--rely-radius-surface)] px-3 text-sm font-medium text-[var(--rely-text-muted)] transition-colors hover:bg-[var(--rely-surface-elevated)] hover:text-[var(--rely-text-strong)]"
                  >
                    <Settings2 className="h-4 w-4" aria-hidden="true" />
                    {t('navigation.companies')}
                  </Link>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
